import React, { useState, useEffect } from 'react';
import { X, Trophy, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';

interface BuyboxModalProps {
  isOpen: boolean;
  onClose: () => void;
  buyboxData: any;
  productData: any;
  tableName?: string;
}

const BuyboxModal: React.FC<BuyboxModalProps> = ({ 
  isOpen, 
  onClose, 
  buyboxData, 
  productData,
  tableName 
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [variants, setVariants] = useState<any[]>([]);
  const [priceStats, setPriceStats] = useState<any>(null);
  const [highlightedPrices, setHighlightedPrices] = useState<Set<number>>(new Set());
  const [highlightType, setHighlightType] = useState<'min' | 'max' | 'range' | null>(null);
  const [activeRangeIndex, setActiveRangeIndex] = useState<number | null>(null);
  
  // Cargar variantes cuando se abre el modal
  useEffect(() => {
    if (isOpen && buyboxData?.buybox_id) {
      loadVariants();
    }
  }, [isOpen, buyboxData?.buybox_id, tableName]);
  
  const loadVariants = async () => {
    if (!buyboxData?.buybox_id) {
      return;
    }
    
    
    setLoading(true);
    try {
      let response;
      
      if (tableName && tableName !== 'comparison') {
        // Si tenemos tableName válido, usar el endpoint específico
        response = await api.get(`/api/tables/${tableName}/buybox/${buyboxData.buybox_id}`);
      } else {
        // Si no tenemos tableName o es 'comparison', usar el nuevo endpoint que busca en todas las tablas
        response = await api.get(`/api/buybox/${buyboxData.buybox_id}/variants`);
      }
      
      const data = response.data;
      
      // Ordenar variantes: winner primero, luego por precio
      const sortedVariants = [...(data.variants || [])].sort((a, b) => {
        const isWinnerA = a.buybox_winner === 1 || a.buybox_winner === '1' || a.buybox_winner === true;
        const isWinnerB = b.buybox_winner === 1 || b.buybox_winner === '1' || b.buybox_winner === true;
        
        if (isWinnerA && !isWinnerB) return -1;
        if (!isWinnerA && isWinnerB) return 1;
        
        const priceA = typeof a.sale_price === 'number' ? a.sale_price : (parseFloat(a.sale_price) || 0);
        const priceB = typeof b.sale_price === 'number' ? b.sale_price : (parseFloat(b.sale_price) || 0);
        return priceA - priceB;
      });
      
      setVariants(sortedVariants);
      
      // Log para debug del campo seller
      if (sortedVariants.length > 0) {
        sortedVariants.slice(0, 5).forEach((variant, index) => {
        });
        
        // Encontrar el ganador específicamente
        const winner = sortedVariants.find(v => v.buybox_winner === 1 || v.buybox_winner === '1');
        if (winner) {
        }
      }
      
      // Calcular estadísticas de precios
      const prices = sortedVariants.map(v => {
        const price = typeof v.sale_price === 'number' ? v.sale_price : (parseFloat(v.sale_price) || 0);
        return price;
      }).filter(p => p > 0);
      
      if (prices.length > 0) {
        const min = Math.min(...prices);
        const max = Math.max(...prices);
        const median = prices[Math.floor(prices.length / 2)];
        
        const range = max - min;
        const bucketSize = range > 0 ? range / 5 : 1;
        const buckets = Array(5).fill(0);
        
        prices.forEach(price => {
          const bucketIndex = Math.min(Math.floor((price - min) / bucketSize), 4);
          buckets[bucketIndex]++;
        });
        
        const maxCount = Math.max(...buckets);
        
        setPriceStats({
          min,
          max,
          median,
          buckets,
          maxCount,
          bucketSize,
          winnerPrice: typeof sortedVariants[0]?.sale_price === 'number' 
            ? sortedVariants[0].sale_price 
            : parseFloat(sortedVariants[0]?.sale_price) || 0
        });
      }
    } catch (error) {
      console.error('Error loading buybox variants:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const formatPrice = (price: any) => {
    if (price === null || price === undefined || price === '') return '-';
    const numPrice = typeof price === 'string' ? parseFloat(price.replace(/[^0-9.-]/g, '')) : price;
    return isNaN(numPrice) ? '-' : `$${numPrice.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div 
        className="bg-white rounded-lg shadow-xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <h3 className="text-xl font-semibold text-gray-900">
              {t('buybox.modal_title', 'Variantes del BuyBox')}
            </h3>
            {variants.length > 0 && (
              <span className="text-sm text-gray-600">
                ({variants.length} {variants.length === 1 ? t('buybox.one_seller', 'vendedor') : t('buybox.multiple_sellers', 'vendedores')})
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Product Info */}
        <div className="mb-6 pb-6 border-b border-gray-200">
          <div className="flex items-start space-x-4">
            {/* Product Image */}
            {(productData.imagen || productData.image) && (
              <div className="flex-shrink-0">
                <img 
                  src={productData.imagen || productData.image}
                  alt={productData.titulo || productData.title || 'Producto'}
                  className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              </div>
            )}
            
            {/* Product Details */}
            <div className="flex-1">
              <h4 className="font-medium text-gray-900 mb-2">
                {productData.titulo || productData.title || 'Producto'}
              </h4>
              <div className="text-sm text-gray-600">
                {productData.Marca || productData.marca} • {productData.sitio || productData.Sitio}
              </div>
            </div>
          </div>
        </div>
        
        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-blue-500 mr-2" />
            <span>{t('buybox.loading_variants', 'Cargando variantes...')}</span>
          </div>
        ) : variants.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {t('buybox.no_variants_found', 'No se encontraron variantes para este producto')}
          </div>
        ) : (
          <>
            {/* Gráfico de rangos de precios */}
            {priceStats && (
              <div className="mb-6 bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center space-x-4 text-sm">
                    <button 
                      className={`cursor-pointer px-2 py-1 rounded transition-colors ${
                        highlightType === 'min' ? 'bg-green-100' : 'hover:bg-green-50'
                      }`}
                      onClick={() => {
                        if (highlightType === 'min') {
                          setHighlightedPrices(new Set());
                          setHighlightType(null);
                        } else {
                          setHighlightedPrices(new Set([priceStats.min]));
                          setHighlightType('min');
                          setActiveRangeIndex(null);
                        }
                      }}
                    >
                      <span className="text-gray-500">{t('buybox.min_price', 'Min')}:</span>
                      <span className="ml-1 font-medium text-gray-900">{formatPrice(priceStats.min)}</span>
                    </button>
                    <div>
                      <span className="text-gray-500">{t('buybox.med_price', 'Med')}:</span>
                      <span className="ml-1 font-medium text-gray-900">{formatPrice(priceStats.median)}</span>
                    </div>
                    <button 
                      className={`cursor-pointer px-2 py-1 rounded transition-colors ${
                        highlightType === 'max' ? 'bg-green-100' : 'hover:bg-green-50'
                      }`}
                      onClick={() => {
                        if (highlightType === 'max') {
                          setHighlightedPrices(new Set());
                          setHighlightType(null);
                        } else {
                          setHighlightedPrices(new Set([priceStats.max]));
                          setHighlightType('max');
                          setActiveRangeIndex(null);
                        }
                      }}
                    >
                      <span className="text-gray-500">{t('buybox.max_price', 'Max')}:</span>
                      <span className="ml-1 font-medium text-gray-900">{formatPrice(priceStats.max)}</span>
                    </button>
                  </div>
                  <div className="flex space-x-1 items-end h-10 w-40">
                    {priceStats.buckets.map((count: number, index: number) => {
                      const height = priceStats.maxCount > 0 ? (count / priceStats.maxCount) * 100 : 0;
                      const winnerPrice = typeof priceStats.winnerPrice === 'number' ? priceStats.winnerPrice : parseFloat(priceStats.winnerPrice);
                      const rangeMin = priceStats.min + index * priceStats.bucketSize;
                      const rangeMax = priceStats.min + (index + 1) * priceStats.bucketSize;
                      const isWinnerInBucket = index === priceStats.buckets.length - 1 
                        ? winnerPrice >= rangeMin && winnerPrice <= rangeMax
                        : winnerPrice >= rangeMin && winnerPrice < rangeMax;
                      const isActive = activeRangeIndex === index;
                      
                      return (
                        <div
                          key={index}
                          className="relative group flex-1 h-full cursor-pointer focus:outline-none"
                          onClick={() => {
                            if (isActive) {
                              setActiveRangeIndex(null);
                              setHighlightedPrices(new Set());
                              setHighlightType(null);
                            } else {
                              const pricesInRange = new Set(
                                variants
                                  .filter(v => {
                                    const price = typeof v.sale_price === 'number' ? v.sale_price : parseFloat(v.sale_price);
                                    if (index === priceStats.buckets.length - 1) {
                                      return price >= rangeMin && price <= rangeMax;
                                    }
                                    return price >= rangeMin && price < rangeMax;
                                  })
                                  .map(v => typeof v.sale_price === 'number' ? v.sale_price : parseFloat(v.sale_price))
                              );
                              setActiveRangeIndex(index);
                              setHighlightedPrices(pricesInRange);
                              setHighlightType('range');
                            }
                          }}
                        >
                          <div className="w-full h-full flex items-end">
                            <div className="w-full relative">
                              {/* Número encima de la barra */}
                              {count > 0 && (
                                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 text-[10px] text-gray-600 z-10">
                                  {count}
                                </div>
                              )}
                              <div
                                className={`w-full rounded-t transition-all ${
                                  isWinnerInBucket ? 'bg-yellow-500' : isActive ? 'bg-green-600' : 'bg-gray-400'
                                }`}
                                style={{ height: `${height * 0.4}px` }}
                              />
                            </div>
                          </div>
                          {/* Tooltip */}
                          <div className="opacity-0 group-hover:opacity-100 absolute bottom-full mb-6 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap transition-opacity pointer-events-none z-10">
                            {count} {count === 1 ? t('buybox.one_seller', 'vendedor') : t('buybox.multiple_sellers', 'vendedores')}
                            <div className="text-yellow-200 text-xs">
                              {formatPrice(rangeMin)} - {formatPrice(rangeMax)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            
            {/* Tabla de variantes */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-y-auto" style={{ maxHeight: '400px' }}>
                <table className="w-full">
                  <thead className="bg-gray-100 text-xs text-gray-700 sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-2 text-center font-medium w-10">#</th>
                      <th className="px-3 py-2 text-left font-medium min-w-[200px]">{t('buybox.vendor_column', 'Vendedor')}</th>
                      <th className="px-3 py-2 text-right font-medium">{t('buybox.original_price_column', 'P. Original')}</th>
                      <th className="px-3 py-2 text-right font-medium">{t('buybox.discounted_price_column', 'P. Desc.')}</th>
                      <th className="px-3 py-2 text-right font-medium">{t('buybox.sale_price_column', 'P. Venta')}</th>
                      <th className="px-3 py-2 text-center font-medium">{t('buybox.installments_column', 'Cuotas')}</th>
                      <th className="px-3 py-2 text-right font-medium">{t('buybox.installment_price_column', 'P. Cuota')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {variants.map((variant, index) => {
                      const isWinner = variant.buybox_winner === 1 || variant.buybox_winner === '1' || variant.buybox_winner === true;
                      // Obtener el seller del campo que se agregará en la importación
                      const sellerValue = variant.seller || 
                                        variant.Seller || 
                                        t('buybox.unknown_seller', 'Vendedor desconocido');
                      const variantPrice = typeof variant.sale_price === 'number' ? variant.sale_price : parseFloat(variant.sale_price);
                      const isHighlighted = highlightedPrices.has(variantPrice);
                      
                      return (
                        <tr 
                          key={`${variant.id_catalogo}-${sellerValue}-${index}`}
                          className={`
                            ${isHighlighted ? 'bg-green-100' : isWinner ? 'bg-yellow-50 font-medium' : 'bg-white'}
                            hover:bg-gray-50 transition-colors
                          `}
                        >
                          <td className="px-3 py-3 text-sm text-center text-gray-500 font-medium">
                            {index + 1}
                          </td>
                          <td className="px-3 py-3 text-sm">
                            <div className="flex items-center">
                              {isWinner && (
                                <Trophy className="h-4 w-4 text-yellow-600 mr-1 flex-shrink-0" />
                              )}
                              <span className={isWinner ? 'text-gray-900' : 'text-gray-700'}>
                                {sellerValue}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-sm text-right text-gray-700">
                            {variant.original_price ? formatPrice(variant.original_price) : '-'}
                          </td>
                          <td className="px-3 py-3 text-sm text-right text-gray-700">
                            {variant.discounted_price ? formatPrice(variant.discounted_price) : '-'}
                          </td>
                          <td className={`px-3 py-3 text-sm text-right font-bold ${isWinner ? 'text-yellow-700' : 'text-gray-900'}`}>
                            {formatPrice(variant.sale_price)}
                          </td>
                          <td className="px-3 py-3 text-sm text-center text-gray-700">
                            {variant.installements || variant.installments || variant.cuotas || '-'}
                          </td>
                          <td className="px-3 py-3 text-sm text-right text-gray-700">
                            {(() => {
                              const existingValue = variant.installements_value || variant.installments_value || variant.precio_cuota;
                              if (existingValue) {
                                return formatPrice(existingValue);
                              }
                              
                              const cuotas = variant.installements || variant.installments || variant.cuotas;
                              if (cuotas && variant.sale_price) {
                                const numCuotas = typeof cuotas === 'string' ? parseInt(cuotas) : cuotas;
                                const salePrice = typeof variant.sale_price === 'string' ? parseFloat(variant.sale_price) : variant.sale_price;
                                if (numCuotas > 0 && salePrice > 0) {
                                  return formatPrice(salePrice / numCuotas);
                                }
                              }
                              
                              return '-';
                            })()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BuyboxModal;